import { InfoIcon } from "lucide-react";
import { useLayoutContext } from "@/components/Layout";
import { usePromptStability } from "@/api/hooks/use-prompt-stability";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { ErrorState } from "@/components/ErrorState";
import { EmptyState } from "@/components/EmptyState";

export function PromptStability() {
  const { tenantId, range } = useLayoutContext();
  const { data, isLoading, isError, refetch } = usePromptStability(tenantId, range);

  if (!tenantId) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-fg">Prompt Stability</h1>
        <EmptyState message="Enter a Tenant ID in the sidebar to get started" />
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-fg">Prompt Stability</h1>
        <LoadingSkeleton cards={3} rows={4} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-fg">Prompt Stability</h1>
        <ErrorState message="Failed to load prompt stability data" onRetry={() => refetch()} />
      </div>
    );
  }

  if (!data || !data.enabled) {
    return (
      <div className="space-y-6">
        <h1 className="text-xl font-semibold text-fg">Prompt Stability</h1>
        <div className="flex items-start gap-3 rounded-lg border border-electric-blue/30 bg-electric-blue/5 p-4">
          <InfoIcon className="h-5 w-5 text-electric-blue shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-electric-blue">Analytics Not Enabled</p>
            <p className="text-sm text-fg-muted mt-1">
              Prompt stability analytics are not enabled for this tenant.
              Contact your administrator to enable provider-side prompt caching analytics.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const families = data.families ?? [];

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-semibold text-fg">Prompt Stability</h1>

      {families.length === 0 ? (
        <EmptyState message="No prompt stability data for this time range" />
      ) : (
        <div className="space-y-3">
          <h2 className="text-sm font-medium text-fg-muted uppercase tracking-wider">
            Prompt Families ({families.length})
          </h2>
          {families.map((family, i) => (
            <div
              key={i}
              className="rounded-lg border border-line-subtle bg-surface p-4"
            >
              <pre className="text-xs text-fg font-mono overflow-x-auto">
                {JSON.stringify(family, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
